import React, {useCallback, useContext, useState} from 'react';

import {MessageContext} from 'src/contexts/message-context';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';
import {useShown} from '@pb/kit-hooks-use-shown';


import {useLoginLinkEmail} from './useLoginLinkEmail';

type Props = {
    userId: number,
    disabled: boolean,
};

const MODAL_CAPTION = 'Send Login Link';
const MODAL_WIDTH = 450;

export const SendLoginEmail = ({userId, disabled}: Props) => {
    const {setSuccessMessage} = useContext(MessageContext);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const {shown, onClick, onClose} = useShown();
    const {loginActionSubmit} = useEventTracking();

    const {
        busy: isPasswordResetEmailSending,
        event: sendPasswordResetEmail,
    } = useLoginLinkEmail({
        userId,
        onSuccess: () => {
            setSuccessMessage('Email with login link is successfully sent');
            loginActionSubmit({submit: true, success: true, itemAction: MODAL_CAPTION});
            onClose();
        },
        onFail: (error: string) => {
            setErrorMessage(error);
            loginActionSubmit({submit: true, success: false, itemAction: MODAL_CAPTION});
        },
    });

    const closeConfirmDialog = useCallback(() => {
        onClose();
        loginActionSubmit({submit: false, success: true, itemAction: MODAL_CAPTION});
    }, [loginActionSubmit, onClose]);

    return (
        <div>
            <Button disabled={disabled} onClick={onClick} variant="Type.SECONDARY">
                Send Login Link
            </Button>
            <Dialog
                shown={shown}
                setShown={closeConfirmDialog}>
                <DialogHeader onClose={closeConfirmDialog}>
                    {MODAL_CAPTION}
                </DialogHeader>
                <DialogMain style={{padding: 24, maxWidth: MODAL_WIDTH}}>
                    {errorMessage ? <Message color="error">{errorMessage}</Message> : null}
                    <p>
                        An email will be sent to this user containing a login link.
                        When a user clicks on this link, they will be taken to a Password Reset
                        page and must change their password before logging in.
                    </p>
                    <p>Note: Login links are one-time use only and expire after 24 hours.</p>
                    <p>Do you want to send the login link?</p>
                </DialogMain>
                <DialogFooter>
                    <Group>
                        <Button
                            variant="Type.PRIMARY"
                            busy={isPasswordResetEmailSending}
                            onClick={() => sendPasswordResetEmail()}>
                            Send
                        </Button>
                        <Button variant="Type.SECONDARY" onClick={closeConfirmDialog}>
                            Cancel
                        </Button>
                    </Group>
                </DialogFooter>
            </Dialog>
        </div>
    );
};
